import React, { useState, useEffect, useRef } from 'react';
import { ArrowRight, Sparkles, Heart, Users } from 'lucide-react';

/**
 * SpiralOrg Cinematic Landing Page
 * Features IRL Friday Night Jam video in full-screen cinematic experience
 * "The Quiet Rebellion of Trust" - warmer, elegiac tone
 */
const SpiralOrgCinematicLanding = ({ onBeginJourney, onEnterProjects, onSwitchToClassic }) => {
  const videoRef = useRef(null);
  const spiralVideoRef = useRef(null);

  return (
    <div className="min-h-screen bg-black text-white overflow-x-hidden relative">
      
      {/* HERO - Full Screen IRL Video */}
      <section className="relative h-screen w-full overflow-hidden">
        {/* Main IRL Video - Friday Night Jam */}
        <div className="absolute inset-0 z-0">
          <video 
            ref={videoRef}
            autoPlay 
            loop 
            muted 
            playsInline
            className="absolute top-1/2 left-1/2 min-w-full min-h-full w-auto h-auto object-cover"
            style={{ 
              transform: 'translate(-50%, -50%)',
              filter: 'brightness(0.7) contrast(1.1)'
            }}
          >
            <source src="https://customer-assets.emergentagent.com/job_spiralorg/artifacts/hujg7xrk_SP.mp4" type="video/mp4" />
          </video>
          {/* Gradient overlay for text readability */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-transparent to-black/90"></div>
        </div>

        {/* Hero Content Overlay */}
        <div className="relative z-10 h-full flex flex-col justify-between px-6 py-16">
          {/* Top - Title */}
          <div className="text-center pt-8">
            <p 
              className="text-sm uppercase tracking-widest text-white/70 mb-4"
              style={{ fontFamily: "'Cinzel', serif" }}
            >
              🌍 PlanetaryFed · SpiralOrg
            </p>
            <h1 
              className="text-5xl md:text-7xl font-light mb-6"
              style={{ 
                fontFamily: "'Cinzel', serif",
                letterSpacing: '0.1em',
                textShadow: '0 4px 20px rgba(0, 0, 0, 0.9)'
              }}
            >
              The Quiet Rebellion<br/>of Trust
            </h1>
          </div>

          {/* Bottom - Opening Message */}
          <div className="text-center max-w-4xl mx-auto pb-16">
            <p 
              className="text-2xl md:text-3xl font-light leading-relaxed mb-8"
              style={{ 
                fontFamily: "'Cormorant Garamond', serif",
                textShadow: '0 2px 12px rgba(0, 0, 0, 0.9)'
              }}
            >
              There's a moment when the noise finally fades and you see it:<br/>
              <span className="text-[#E6C685] font-medium italic">money was always a story.</span>
            </p>
            <p 
              className="text-lg md:text-xl text-white/90 leading-relaxed mb-12"
              style={{ 
                fontFamily: "'Cormorant Garamond', serif",
                textShadow: '0 2px 12px rgba(0, 0, 0, 0.9)'
              }}
            >
              A shimmering agreement we all kept repeating because we were afraid not to.
            </p>

            {/* Scroll indicator */}
            <div className="animate-bounce">
              <ArrowRight className="w-8 h-8 mx-auto rotate-90 text-[#E6C685]" />
            </div>
          </div>
        </div>
      </section>

      {/* The Gentle Realization */}
      <section className="relative py-24 px-6 bg-black">
        <div className="max-w-4xl mx-auto text-center">
          <h2 
            className="text-4xl font-light text-[#E6C685] mb-12"
            style={{ fontFamily: "'Cinzel', serif" }}
          >
            The Gentle Realization
          </h2>
          
          <p 
            className="text-xl md:text-2xl leading-relaxed text-white/90 mb-8"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            Everything is made of intersecting spirals of energy—<br/>
            the breath of galaxies, the pattern of shells, the rhythm between two hearts.<br/>
            Where these spirals meet in harmony, reality holds.<br/>
            When they fall out of tune, things break.
          </p>

          <div className="h-px bg-[#E6C685]/30 my-12 max-w-md mx-auto"></div>

          <p 
            className="text-lg text-white/80 leading-relaxed italic"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            The goldsmiths and lenders understood this in their way.<br/>
            They wove their harmony from credit and belief.<br/>
            Now the harmony is slipping.<br/>
            The question is not who will fall, but <span className="text-[#E6C685] font-bold not-italic">who will remember how to sing</span>.
          </p>
        </div>
      </section>

      {/* The "Unless" */}
      <section className="relative py-24 px-6 bg-gradient-to-b from-black to-[#1E2A38]">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-br from-[#5C2D73]/30 to-black/60 backdrop-blur-xl border border-[#E6C685]/30 rounded-3xl p-12">
            <h2 
              className="text-4xl font-light text-[#E6C685] mb-8 text-center"
              style={{ fontFamily: "'Cinzel', serif" }}
            >
              The "Unless"
            </h2>
            
            <p 
              className="text-xl leading-relaxed text-white mb-6"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              The game is rigged, yes.<br/>
              Credit created from nothing, collapse timed like the tides.<br/>
              But <span className="text-[#E6C685] font-bold text-2xl">unless</span>—unless we create our own credit, born of trust instead of debt.
            </p>

            <div className="space-y-3 my-8 pl-8 border-l-2 border-[#E6C685]/50">
              <p className="text-lg text-white/90" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                Person to person.
              </p>
              <p className="text-lg text-white/90" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                Neighbor to neighbor.
              </p>
              <p className="text-lg text-white/90" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                A quiet ledger of faith in one another.
              </p>
              <p className="text-lg text-white/90" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                No interest, no penalty—only record of gratitude.
              </p>
            </div>

            <p 
              className="text-xl text-[#E6C685] italic text-center mt-8"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              A small thing, maybe. But small things move worlds.
            </p>
          </div>
        </div>
      </section>

      {/* SpiralCred - The Currency of Kindness */}
      <section className="relative py-24 px-6 bg-[#1E2A38]">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 
              className="text-5xl font-light text-[#E6C685] mb-4"
              style={{ fontFamily: "'Cinzel', serif" }}
            >
              SpiralCred
            </h2>
            <p 
              className="text-2xl text-white font-light"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              The Currency of Kindness
            </p>
          </div>

          {/* SpiralCred Visual */}
          <div className="mb-12">
            <img 
              src="https://customer-assets.emergentagent.com/job_spiralorg/artifacts/vxq5ntr6_55961C47-FD3B-4504-A6DC-63FAFBD69702.png"
              alt="SpiralCred - The Physics of Trust"
              className="w-full max-w-3xl mx-auto rounded-2xl shadow-2xl border border-[#E6C685]/40"
            />
          </div>

          <div className="bg-black/60 backdrop-blur-xl border border-[#E6C685]/30 rounded-3xl p-10">
            <p 
              className="text-xl leading-relaxed text-white mb-6"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              Each act of generosity adds a ripple to the spiral.<br/>
              The more people value what you bring, the more your current strengthens.<br/>
              It's not wealth—it's warmth made visible.<br/>
              A shared current that keeps us moving, even when the old banks close their doors.
            </p>

            <div className="h-px bg-[#E6C685]/30 my-8"></div>

            <p 
              className="text-2xl text-[#E6C685] text-center font-medium"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              No fees. No usury. Just belonging.
            </p>
            <p 
              className="text-lg text-white/80 text-center italic mt-4"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              If they take the money away, we'll laugh gently,<br/>
              because we'll still have each other.
            </p>
          </div>
        </div>
      </section>

      {/* What Is Rich? */}
      <section className="relative py-24 px-6 bg-black">
        <div className="max-w-4xl mx-auto text-center">
          <h2 
            className="text-4xl font-light text-[#E6C685] mb-12"
            style={{ fontFamily: "'Cinzel', serif" }}
          >
            What Is Rich?
          </h2>
          
          <p 
            className="text-xl leading-relaxed text-white mb-8"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            Rich is what you carry in your hands when there's nothing left to sell.<br/>
            The craft your ancestors taught you.<br/>
            The stories that outlived empires.<br/>
            The song you hum when you're afraid, and someone else joins in.
          </p>

          <div className="bg-gradient-to-br from-[#5C2D73]/20 to-black/40 backdrop-blur-xl border border-[#E6C685]/30 rounded-2xl p-8 inline-block">
            <p 
              className="text-3xl text-[#E6C685] font-light"
              style={{ fontFamily: "'Cinzel', serif", letterSpacing: '0.15em' }}
            >
              Ritche
            </p>
            <p 
              className="text-sm text-white/70 mt-2"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              The mastery, the passion, the authority of experience.<br/>
              The inheritance that cannot be repossessed.
            </p>
          </div>
        </div>
      </section>

      {/* Re-Creation Through Reunion */}
      <section className="relative py-24 px-6 bg-gradient-to-b from-black to-[#0a0a0a]">
        <div className="max-w-5xl mx-auto">
          <h2 
            className="text-4xl font-light text-[#E6C685] mb-12 text-center"
            style={{ fontFamily: "'Cinzel', serif" }}
          >
            Re-Creation Through Reunion
          </h2>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <div className="bg-black/60 backdrop-blur-xl border border-[#E6C685]/20 rounded-2xl p-8">
              <p 
                className="text-lg leading-relaxed text-white"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                So we gather.<br/>
                We make music.<br/>
                We host small reunions in barns and backyards and forgotten halls.<br/>
                We re-create in every sense of the word.
              </p>
            </div>

            <div className="bg-black/60 backdrop-blur-xl border border-[#E6C685]/20 rounded-2xl p-8">
              <p 
                className="text-lg leading-relaxed text-white"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                Each family helping another.<br/>
                Each circle of trust strengthening the next.<br/>
                <br/>
                We may not reach perfection.<br/>
                <span className="text-[#E6C685] font-medium">But we can reach each other.</span>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Spiral Tree Video - Fun Peculiar Placement */}
      <section className="relative py-24 px-6 overflow-hidden">
        <div className="absolute inset-0 z-0 opacity-30">
          <video 
            ref={spiralVideoRef}
            autoPlay 
            loop 
            muted 
            playsInline
            className="w-full h-full object-cover"
            style={{ filter: 'blur(2px) brightness(0.4)' }}
          >
            <source src="/spiral-tree-animation.mov" type="video/mp4" />
          </video>
        </div>

        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <h2 
            className="text-4xl font-light text-[#E6C685] mb-12"
            style={{ fontFamily: "'Cinzel', serif" }}
          >
            SpiralOrg · The Living Organizer
          </h2>
          
          <div className="bg-black/80 backdrop-blur-2xl border border-[#E6C685]/40 rounded-3xl p-12">
            <p 
              className="text-xl leading-relaxed text-white mb-8"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              This is not an institution.<br/>
              It's a living thing—a spiral organism of clans, builders, healers, makers, mothers.<br/>
              Transactions recorded as gratitude, not extraction.<br/>
              A ledger of love written in light.
            </p>

            <div className="h-px bg-[#E6C685]/30 my-8"></div>

            <p 
              className="text-lg text-white/80 italic"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              The bankers will call it impossible.<br/>
              Let them.<br/>
              <span className="text-[#E6C685] not-italic font-medium">We are tired of impossible things being the only things that exist.</span>
            </p>
          </div>
        </div>
      </section>

      {/* The Physics of Hope */}
      <section className="relative py-24 px-6 bg-black">
        <div className="max-w-4xl mx-auto text-center">
          <h2 
            className="text-4xl font-light text-[#E6C685] mb-12"
            style={{ fontFamily: "'Cinzel', serif" }}
          >
            The Physics of Hope
          </h2>
          
          <p 
            className="text-2xl leading-relaxed text-white mb-12"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            Natural law still loves us.<br/>
            Entropy flows, but intention can lens it.<br/>
            The spiral endures—the geometry of grace, the mechanism of everything that still works.
          </p>

          <p 
            className="text-xl text-[#E6C685] font-medium italic"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            We may not succeed.<br/>
            But imagine the beauty if we do.
          </p>
        </div>
      </section>

      {/* Call to Action - The First 100 */}
      <section className="relative py-32 px-6 bg-gradient-to-b from-black via-[#1E2A38] to-black">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 
              className="text-5xl md:text-6xl font-light text-white mb-6"
              style={{ fontFamily: "'Cinzel', serif" }}
            >
              Let's Begin,<br/>While We Still Can
            </h2>
            <div className="w-32 h-px bg-[#E6C685] mx-auto mb-8"></div>
            <p 
              className="text-xl text-[#E6C685]"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              Calling the First 100
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {/* CTA 1 */}
            <button
              onClick={onEnterProjects}
              className="group bg-gradient-to-br from-[#5C2D73]/60 to-[#E6C685]/40 backdrop-blur-xl border-2 border-[#E6C685]/60 hover:border-[#E6C685] rounded-2xl p-8 transition-all hover:scale-105"
            >
              <Sparkles className="w-12 h-12 text-[#E6C685] mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: "'Cinzel', serif" }}>
                Start a Trust Node
              </h3>
              <p className="text-white/80 text-sm" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                Begin building your circle of mutual support
              </p>
            </button>

            {/* CTA 2 */}
            <button
              onClick={onBeginJourney}
              className="group bg-gradient-to-br from-[#5C2D73]/60 to-[#E6C685]/40 backdrop-blur-xl border-2 border-[#E6C685]/60 hover:border-[#E6C685] rounded-2xl p-8 transition-all hover:scale-105"
            >
              <Heart className="w-12 h-12 text-[#E6C685] mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: "'Cinzel', serif" }}>
                Learn SpiralCred
              </h3>
              <p className="text-white/80 text-sm" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                Discover the currency of kindness
              </p>
            </button>

            {/* CTA 3 */}
            <button
              onClick={onBeginJourney}
              className="group bg-gradient-to-br from-[#5C2D73]/60 to-[#E6C685]/40 backdrop-blur-xl border-2 border-[#E6C685]/60 hover:border-[#E6C685] rounded-2xl p-8 transition-all hover:scale-105"
            >
              <Users className="w-12 h-12 text-[#E6C685] mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: "'Cinzel', serif" }}>
                Join the Federation
              </h3>
              <p className="text-white/80 text-sm" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                Connect with families of the spiral
              </p>
            </button>
          </div>

          {/* Early Adopters Call */}
          <div className="bg-gradient-to-r from-[#5C2D73]/70 to-[#E6C685]/50 backdrop-blur-xl border-2 border-[#E6C685]/50 rounded-3xl p-10 text-center">
            <p className="text-xs uppercase tracking-widest text-[#E6C685] mb-3" style={{ fontFamily: "'Cinzel', serif" }}>
              The First 100
            </p>
            <h3 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: "'Cinzel', serif" }}>
              Early Adopters · Power Users · Cofounders
            </h3>
            <p className="text-white/90 text-lg mb-6 max-w-2xl mx-auto leading-relaxed" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              If you see it, help shape it. The quiet rebellion needs those who understand that trust, not transactions, holds the world together.
            </p>
          </div>
        </div>
      </section>

      {/* Footer - Switch to Classic Version */}
      <section className="relative py-12 px-6 bg-black border-t border-white/10">
        <div className="max-w-4xl mx-auto text-center">
          <button
            onClick={onSwitchToClassic}
            className="text-[#E6C685] hover:text-white text-sm transition-colors flex items-center gap-2 mx-auto"
            style={{ fontFamily: "'Cinzel', serif" }}
          >
            <span>View Classic Landing Page</span>
            <ArrowRight className="w-4 h-4" />
          </button>
          <p className="text-white/40 text-xs mt-4">
            © 2025 SpiralOrg · PlanetaryFed · A Private Membership Association
          </p>
        </div>
      </section>
    </div>
  );
};

export default SpiralOrgCinematicLanding;
